/**
 * Light or dark, and which hue, remembered.
 *
 * Both stamps already exist on <html> before React starts (see index.html),
 * so this module never sets the initial values -- it READS what the bootstrap
 * decided. Writing them here as well would re-apply on mount and reintroduce
 * exactly the flash the bootstrap removes.
 */
import { useEffect, useState } from 'react'

/**
 * Three, since 2026-10-10. `white` is NOT a brighter light: light's canvas is
 * a blue-grey that cards lift off, and ultra white's canvas is white with the
 * card told apart by its hairline. The owner asked for it explicitly and
 * asked for light to be left alone, so both exist.
 */
export type Theme = 'light' | 'dark' | 'white'

/** Cycle order for the app-bar button: lightest to darkest. */
export const THEMES: Theme[] = ['light', 'white', 'dark']

export const THEME_LABEL: Record<Theme, string> = {
  light: 'theme.light',
  white: 'theme.white',
  dark: 'theme.dark',
}

/**
 * The accent, and with it the neutral tint of the dark shell.
 *
 * The owner, on 2026-10-09: *"it is greenish some people might not like it"*.
 * Green stays the default -- it is the app's look -- but it is a preference
 * now rather than a fact. `slate` is the no-hue option.
 */
export type Accent =
  | 'green' | 'cobalt' | 'teal' | 'plum' | 'slate'
  // The soft five, 2026-10-10. Desaturated rather than pale -- the accent is
  // a button background with white text on it.
  | 'sand' | 'rose' | 'sky' | 'sage' | 'clay'

export const ACCENTS: Accent[] = [
  'green', 'cobalt', 'teal', 'plum', 'slate',
  'sand', 'rose', 'sky', 'sage', 'clay',
]

/** The i18n key naming each one, for the picker. */
export const ACCENT_LABEL: Record<Accent, string> = {
  green: 'theme.accentGreen',
  cobalt: 'theme.accentCobalt',
  teal: 'theme.accentTeal',
  plum: 'theme.accentPlum',
  slate: 'theme.accentSlate',
  sand: 'theme.accentSand',
  rose: 'theme.accentRose',
  sky: 'theme.accentSky',
  sage: 'theme.accentSage',
  clay: 'theme.accentClay',
}

/**
 * The swatch colour, per accent and theme.
 *
 * Deliberately NOT read from the live `--c-accent`: every swatch has to show
 * its own hue at once, and there is only one variable. These are the same
 * values index.css carries, and the comment there says why they are twinned.
 */
export const ACCENT_SWATCH: Record<Accent, { light: string; dark: string }> = {
  green: { light: 'rgb(36 110 78)', dark: 'rgb(111 193 148)' },
  cobalt: { light: 'rgb(31 79 216)', dark: 'rgb(125 162 255)' },
  teal: { light: 'rgb(13 126 125)', dark: 'rgb(90 202 198)' },
  plum: { light: 'rgb(123 63 170)', dark: 'rgb(191 150 240)' },
  slate: { light: 'rgb(71 85 105)', dark: 'rgb(177 190 207)' },
  sand: { light: 'rgb(141 106 56)', dark: 'rgb(214 180 128)' },
  rose: { light: 'rgb(167 71 96)', dark: 'rgb(238 160 180)' },
  sky: { light: 'rgb(43 115 170)', dark: 'rgb(132 190 235)' },
  sage: { light: 'rgb(89 118 92)', dark: 'rgb(169 196 171)' },
  clay: { light: 'rgb(171 86 57)', dark: 'rgb(234 160 134)' },
}

/**
 * The swatch to draw for a theme. Ultra white shares light's accents -- the
 * accent blocks in index.css select on :not([data-theme="dark"]), so there is
 * only ever a light set and a dark set.
 */
export function swatchFor(accent: Accent, theme: Theme): string {
  return ACCENT_SWATCH[accent][theme === 'dark' ? 'dark' : 'light']
}

const KEY = 'tb.theme'
const ACCENT_KEY = 'tb.accent'

function isAccent(v: unknown): v is Accent {
  return typeof v === 'string' && (ACCENTS as string[]).includes(v)
}

export function currentTheme(): Theme {
  const stamped = document.documentElement.getAttribute('data-theme')
  if (stamped === 'light') return 'light'
  if (stamped === 'white') return 'white'
  return 'dark'
}

export function currentAccent(): Accent {
  const stamped = document.documentElement.getAttribute('data-accent')
  return isAccent(stamped) ? stamped : 'green'
}

/**
 * The browser chrome colour, so the phone's status bar and the PWA splash
 * follow the choice instead of staying on the cobalt this file was born with.
 */
function paintChrome(theme: Theme, accent: Accent): void {
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', swatchFor(accent, theme))
}

export function setTheme(t: Theme): void {
  document.documentElement.setAttribute('data-theme', t)
  paintChrome(t, currentAccent())
  // Storage can throw (private mode, blocked site data). The theme still
  // applies for this session; it simply will not be remembered.
  try { localStorage.setItem(KEY, t) } catch { /* not fatal */ }
}

export function setAccent(a: Accent): void {
  document.documentElement.setAttribute('data-accent', a)
  paintChrome(currentTheme(), a)
  try { localStorage.setItem(ACCENT_KEY, a) } catch { /* not fatal */ }
}

/**
 * `const [theme, choose] = useTheme()`.
 *
 * A SETTER, not a toggle. Two themes could be flipped; three cannot, and a
 * caller that wants the next one asks for `nextTheme(theme)` and says so.
 */
export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, set] = useState<Theme>(currentTheme)
  // Another tab flipping the theme should not leave this one disagreeing with
  // what is now in storage.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== KEY) return
      const t: Theme = e.newValue === 'light' ? 'light'
        : e.newValue === 'white' ? 'white' : 'dark'
      document.documentElement.setAttribute('data-theme', t)
      set(t)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
  return [theme, (t: Theme) => { setTheme(t); set(t) }]
}

/** The one the app-bar button moves to: light -> white -> dark -> light. */
export function nextTheme(t: Theme): Theme {
  return THEMES[(THEMES.indexOf(t) + 1) % THEMES.length]!
}

/** `const [accent, choose] = useAccent()`. */
export function useAccent(): [Accent, (a: Accent) => void] {
  const [accent, set] = useState<Accent>(currentAccent)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACCENT_KEY) return
      const a: Accent = isAccent(e.newValue) ? e.newValue : 'green'
      document.documentElement.setAttribute('data-accent', a)
      set(a)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
  return [accent, (a: Accent) => { setAccent(a); set(a) }]
}
